// LawyerSearchLanding.tsx
import { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, MapPin, User2, Building2, X } from 'lucide-react';
import Bowser from 'bowser';
import { newServer } from '../../../src/constants/constants';
import AnimalCareFAQSection from './LawyerFAQSection';

interface Suggestion {
  care_provider_id?: string;
  submerchant_id?: string;
  name?: string;
  type?: string;
  submerchant_name?: string;
  specialtyArea?: string;
}

interface Skill {
  nombre: string;
  id: number;
}

interface Language {
  nombre: string;
  id: number;
}

interface ProfessionalType {
  id: number;
  title: string;
  description: string;
  handles: string;
  example: string;
}

interface DirectoryData {
  skills: Skill[];
  lenguages: Language[];
  filter: { setting_enable_rating: boolean }[];
  professionalTypes: ProfessionalType[];
}

// Función que simula la generación de keywords para cuidado de animales
const getSeoKeywords = (locale: string): string => {
  if (locale === 'es') {
    return 'Cuidado Animal, Veterinarios, Refugios, Adopción, Rescate Animal';
  } else {
    return 'Animal Care, Veterinarians, Shelters, Pet Adoption, Animal Rescue';
  }
};

const AnimalCareSearchLanding = () => {
  const { t, i18n } = useTranslation();
  const [selectedSuggestion, setSelectedSuggestion] = useState<Suggestion | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [location, setLocation] = useState('');
  const [isOnline, setIsOnline] = useState(false);
  const [searchType, setSearchType] = useState('careprovider');
  const [isFocused, setIsFocused] = useState(false);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [directoryData, setDirectoryData] = useState<DirectoryData | null>(null);
  const [showDirectoryData, setShowDirectoryData] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Obtenemos el idioma actual desde i18next
  const locale = i18n.language; // por ejemplo: 'en' o 'es'
  console.log('Current locale:', locale);
  const seoKeywords = getSeoKeywords(locale);

  // Función para buscar sugerencias basadas en el término
  const fetchSuggestions = async (term: string) => {
    if (!term || term.length < 2) {
      setSuggestions([]);
      return;
    }
    setLoading(true);
    try {
      const endpoint = `${newServer}directory/search-suggestions?type=${searchType}&query=${encodeURIComponent(
        term
      )}`;
      const response = await fetch(endpoint);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setSuggestions(data.suggestions || []);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  // Función para obtener datos del directorio (habilidades, idiomas)
  const fetchDirectoryData = async () => {
    setLoading(true);
    try {
      const currentLanguage = i18n.language || 'en'; // Obtener idioma actual
      const endpoint = `${newServer}directory/?lang=${currentLanguage}`;
      const response = await fetch(endpoint);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setDirectoryData(data);
    } catch (error) {
      console.error('Error fetching directory data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDirectoryData();
  }, [i18n.language]); // Re-ejecutar cuando cambie el idioma

  // Manejar clicks fuera del componente de autocompletado
  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
        setShowDirectoryData(false);
        setIsFocused(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Mostrar sugerencias o directorio según el foco y el término
  useEffect(() => {
    if (isFocused) {
      if (searchTerm.length > 0) {
        fetchSuggestions(searchTerm);
        setShowSuggestions(true);
        setShowDirectoryData(false);
      } else {
        setShowDirectoryData(true);
        setShowSuggestions(false);
      }
    }
  }, [searchTerm, isFocused, searchType]);

  const handleSearch = () => {
    let params: Record<string, string> = {};
    if (selectedSuggestion) {
      if (searchType === 'careprovider' && selectedSuggestion.care_provider_id) {
        params = {
          type: 'individual',
          care_provider_id: selectedSuggestion.care_provider_id,
          submerchant_id: selectedSuggestion.submerchant_id || '',
          search: selectedSuggestion.name || '',
          ...(isOnline ? {} : { location }),
          online: isOnline.toString(),
        };
      } else if (searchType === 'facility' && selectedSuggestion.submerchant_id) {
        params = {
          type: 'facility',
          submerchant_id: selectedSuggestion.submerchant_id,
          search: selectedSuggestion.name || '',
          ...(isOnline ? {} : { location }),
          online: isOnline.toString(),
        };
      } else {
        params = {
          type: searchType,
          search: searchTerm,
          ...(isOnline ? {} : { location }),
          online: isOnline.toString(),
        };
      }
    } else {
      params = {
        type: searchType,
        search: searchTerm,
        ...(isOnline ? {} : { location }),
        online: isOnline.toString(),
      };
    }
    const queryParams = new URLSearchParams(params);
    saveGeoip();
    navigate(`/directory/search?${queryParams.toString()}`);
  };

  const handleSuggestionClick = (suggestion: Suggestion) => {
    setSearchTerm(suggestion.name || '');
    setSelectedSuggestion(suggestion);
    setShowSuggestions(false);
    setShowDirectoryData(false);
  };

  const handleSkillOrLanguageClick = (name: string) => {
    setSearchTerm(name);
    setSelectedSuggestion({ name });
    setShowSuggestions(false);
    setShowDirectoryData(false);
  };

  const handleInputFocus = () => {
    setIsFocused(true);
    if (!searchTerm || searchTerm.length === 0) {
      setShowDirectoryData(true);
      setShowSuggestions(false);
    } else {
      setShowSuggestions(true);
      setShowDirectoryData(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    setSelectedSuggestion(null);
    if (value.length > 0) {
      setShowDirectoryData(false);
      setShowSuggestions(value.length >= 2);
    } else {
      setShowDirectoryData(true);
      setShowSuggestions(false);
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
    setSuggestions([]);
    setShowSuggestions(false);
    setSelectedSuggestion(null);
    setShowDirectoryData(isFocused);
  };

  const saveGeoip = async () => {
    if (typeof window === 'undefined') return;
    const infoNav = Bowser.parse(window.navigator.userAgent);
    const medium = 'Directory';
    const requestData = { ...infoNav, medium };
    const subdomain = process.env.NEXT_PUBLIC_SUBDOMAIN || 'defaultSubdomain';
    try {
      const response = await fetch(
        `${newServer}browser-metrics/save-browser-info-directory?submerchant=${subdomain}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestData),
        }
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result = await response.json();
      console.log('browser_info=>', result);
    } catch (error) {
      console.error('browser_info=>', error);
    }
  };

  return (
    <>
      <Helmet>
        <title>{t('title').toString()}</title>
        <meta name='description' content={t('description')} />
        <meta name='keywords' content={seoKeywords} />
        <link
          rel='alternate'
          hrefLang={locale.startsWith('es') ? 'es-co' : 'en-us'}
          href={window.location.origin + window.location.pathname}
        />
        <script type='application/ld+json'>
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Service',
            name: locale.startsWith('es') ? 'Servicios de Cuidado Animal - Noah\'s Ark' : 'Animal Care Services - Noah\'s Ark',
            address: {
              '@type': 'PostalAddress',
              streetAddress: locale.startsWith('es') ? 'Servicios de Cuidado Animal' : 'Animal Care Services',
              addressLocality: locale.startsWith('es') ? 'México' : 'United States',
              addressCountry: locale.startsWith('es') ? 'MX' : 'US',
            },
            areaServed: [locale.startsWith('es') ? 'MX' : 'US'],
            legalName: locale.startsWith('es') ? 'Noah\'s Ark - Cuidado Animal' : 'Noah\'s Ark - Animal Care',
            sameAs: [window.location.origin],
          })}
        </script>
      </Helmet>
      <div
        style={{ fontFamily: 'Poppins, sans-serif' }}
        className='relative min-h-screen bg-gradient-to-r from-pink-200 via-purple-50 to-pink-100'
      >
        {/* Background Shapes */}
        <div className='fixed top-[-100px] right-[-100px] w-[300px] h-[300px] bg-pink-400 opacity-40 rounded-full blur-3xl'></div>
        <div className='fixed bottom-[-100px] left-[-100px] w-[300px] h-[300px] bg-purple-400 opacity-40 rounded-full blur-3xl'></div>
        {/* Header Section */}
        <div className='relative'>
          <div className='max-w-6xl mx-auto px-4 py-8 sm:px-6 lg:px-8'>
            <h1
              className='text-5xl sm:text-4xl font-bold text-slate-900 text-center'
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              {t('find_legal_support').toString()}
            </h1>
            <p
              className='mt-3 text-center text-slate-600 max-w-2xl mx-auto'
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              {t('connect_verified').toString()}
            </p>
          </div>
        </div>
        {/* Main Search Section */}
        <div className='max-w-3xl sm:max-w-7xl md:max-w-4xl lg:max-w-4xl mx-auto px-4 py-8 sm:px-6 lg:px-8'>
          {/* Search Type Tabs */}
          <div className='flex justify-center mb-6'>
            <div className='bg-white rounded-lg shadow-sm p-1 inline-flex'>
              <button
                className={`flex items-center px-4 py-2 rounded-md transition-all ${
                  searchType === 'careprovider'
                    ? 'bg-pink-50 text-pink-700'
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
                onClick={() => {
                  setSearchType('careprovider');
                  clearSearch();
                }}
              >
                <User2 className='w-4 h-4 mr-2' />
                <span className='font-medium'>{t('individual_care_provider').toString()}</span>
              </button>
              <button
                className={`flex items-center px-4 py-2 rounded-md transition-all ${
                  searchType === 'facility'
                    ? 'bg-pink-50 text-pink-700'
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
                onClick={() => {
                  setSearchType('facility');
                  clearSearch();
                }}
              >
                <Building2 className='w-4 h-4 mr-2' />
                <span className='font-medium'>{t('animal_care_facility').toString()}</span>
              </button>
            </div>
          </div>
          {/* Search Box */}
          <div className='bg-white rounded-xl shadow-lg transition-all'>
            <div className='p-4'>
              <div className='space-y-4'>
                <div className='relative' ref={searchRef}>
                  <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                    <Search className='h-5 w-5 text-slate-400' />
                  </div>
                  <input
                    type='text'
                    placeholder={t('search_care_provider_placeholder').toString()}
                    style={{ fontFamily: 'Poppins, sans-serif', fontSize: '16px' }}
                      className='block w-full pl-10 pr-10 py-3 border border-slate-200 rounded-lg focus:outline-none focus:border-pink-500 focus:ring-1 focus:ring-pink-500'
                    value={searchTerm}
                    onChange={handleInputChange}
                    onFocus={handleInputFocus}
                    inputMode='text'
                  />
                  {searchTerm && (
                    <button
                      className='absolute inset-y-0 right-0 pr-3 flex items-center'
                      onClick={clearSearch}
                    >
                      <X className='h-5 w-5 text-slate-400 hover:text-slate-600' />
                    </button>
                  )}
                  {/* Directory Data (Skills y Languages) */}
                  {showDirectoryData && directoryData && (
                    <div className='absolute z-10 w-full bg-white mt-1 rounded-md shadow-lg border border-slate-200 max-h-96 overflow-y-auto'>
                      {loading ? (
                        <div className='p-3 text-center text-slate-500'>Loading...</div>
                      ) : (
                        <div className='p-2'>
                          {/* Skills Section */}
                          <div className='mb-4'>
                            <h3 className='text-sm font-semibold text-slate-700 px-2 py-1 bg-slate-100 rounded'>
                              Animal Care Specialties
                            </h3>
                            <div className='mt-2 flex flex-wrap gap-2 px-2'>
                              {directoryData.skills.map((skill) => (
                                <button
                                  key={skill.id}
                                  className='px-3 py-1 bg-pink-50 text-pink-700 rounded-full text-sm hover:bg-pink-100 transition-colors'
                                  onClick={() => handleSkillOrLanguageClick(skill.nombre)}
                                >
                                  {skill.nombre}
                                </button>
                              ))}
                            </div>
                          </div>
                          {/* Languages Section */}
                          <div>
                            <h3 className='text-sm font-semibold text-slate-700 px-2 py-1 bg-slate-100 rounded'>
                              Languages
                            </h3>
                            <div className='mt-2 flex flex-wrap gap-2 px-2'>
                              {directoryData.lenguages.map((language) => (
                                <button
                                  key={language.id}
                                  className='px-3 py-1 bg-pink-50 text-pink-700 rounded-full text-sm hover:bg-pink-100 transition-colors'
                                  onClick={() => handleSkillOrLanguageClick(language.nombre)}
                                >
                                  {language.nombre}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {/* Autocomplete Suggestions */}
                  {showSuggestions && suggestions.length > 0 && (
                    <div className='absolute z-10 w-full bg-white mt-1 rounded-md shadow-lg border border-slate-200 max-h-60 overflow-y-auto'>
                      {loading ? (
                        <div className='p-3 text-center text-slate-500'>Loading...</div>
                      ) : (
                        <ul>
                          {suggestions.map((suggestion, index) => (
                            <li
                              key={index}
                              className='px-4 py-2 hover:bg-blue-50 cursor-pointer text-slate-700 border-b border-slate-100 last:border-0'
                              onClick={() => handleSuggestionClick(suggestion)}
                            >
                              <div className='flex items-center'>
                                {searchType === 'careprovider' && !suggestion.type ? (
                                  <User2 className='h-4 w-4 text-slate-400 mr-2 flex-shrink-0' />
                                ) : searchType === 'facility' || suggestion.type === 'specialty' ? (
                                  <Building2 className='h-4 w-4 text-slate-400 mr-2 flex-shrink-0' />
                                ) : (
                                  <Search className='h-4 w-4 text-slate-400 mr-2 flex-shrink-0' />
                                )}
                                <div className='flex flex-col'>
                                  <span className='font-medium'>{suggestion.name || ''}</span>
                                  {suggestion.submerchant_name && (
                                    <span className='text-slate-500 text-xs'>
                                      {suggestion.submerchant_name}
                                    </span>
                                  )}
                                  {suggestion.specialtyArea && (
                                    <span className='text-slate-500 text-xs'>
                                      {suggestion.specialtyArea}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
                {!isOnline && (
                  <div className='relative'>
                    <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                      <MapPin className='h-5 w-5 text-slate-400' />
                    </div>
                    <input
                      type='text'
                      placeholder={t('city_location_placeholder').toString()}
                      style={{ fontFamily: 'Poppins, sans-serif', fontSize: '16px' }}
                      className='block w-full pl-10 pr-3 py-3 border border-slate-200 rounded-lg focus:outline-none focus:border-pink-500 focus:ring-1 focus:ring-pink-500'
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      inputMode='text'
                    />
                  </div>
                )}
                <div className='flex items-center'>
                  <input
                    id='isOnlineCheckbox'
                    type='checkbox'
                    className='h-4 w-4 text-blue-600 focus:ring-blue-500 border-slate-300 rounded'
                    checked={isOnline}
                    onChange={(e) => setIsOnline(e.target.checked)}
                  />
                  <label
                    htmlFor='isOnlineCheckbox'
                    className='ml-2 block text-sm text-slate-600'
                    style={{ fontFamily: 'Poppins, sans-serif' }}
                  >
                    {t('online_virtual_services_only').toString()}
                  </label>
                </div>
                <button
                  onClick={handleSearch}
                  className='w-full bg-pink-600 hover:bg-pink-700 text-white font-medium py-3 px-4 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2'
                >
                  {t('search_button').toString()}
                </button>
              </div>
            </div>
            {/* Search Tips */}
            <div className='px-4 py-3 bg-slate-50/80 rounded-b-xl border-t border-slate-100'>
              <p className='text-sm text-slate-600' style={{ fontFamily: 'Poppins, sans-serif' }}>
                {searchType === 'careprovider'
                  ? t('search_description_individual').toString()
                  : t('search_description_facility').toString()}
              </p>
            </div>
          </div>
        </div>
        {/* FAQ Section */}
        <AnimalCareFAQSection />
      </div>
    </>
  );
};

export default AnimalCareSearchLanding;
